import React, { useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import firebase from '../store/firebase'
import { calculateTestPassed, calculateLCEarned } from '../utils/test_utils'
import adriaanSignature from '../assets/signature/adriaan.png'

function MyTestResume({ userdata }) {
  // Simple mobile detection using window.matchMedia; defaults to false during SSR
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false))
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 640px)')
    const handler = (e) => setIsMobile(e.matches)
    if (mql.addEventListener) mql.addEventListener('change', handler)
    else if (mql.addListener) mql.addListener(handler)
    setIsMobile(mql.matches)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler)
      else if (mql.removeListener) mql.removeListener(handler)
    }
  }, [])
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const printRef = useRef(null)
  // Simple, adjustable policy for certificate qualification
  // Explicit LC thresholds by level (1..5)
   var LEVEL_THRESHOLDS
  // if (userdata.email==='adriaan@leadershipplatform.com') {
  //    // LEVEL_THRESHOLDS = [3, 8, 12, 18, 30]
  //     LEVEL_THRESHOLDS = [3, 10, 18, 30, 45]
  // }
  // else {
  //   // LEVEL_THRESHOLDS = [0.5, 1, 1.5, 2.6, 3.2]
  //    // LEVEL_THRESHOLDS = [4, 8, 12, 18, 30]
  //     LEVEL_THRESHOLDS = [5, 10, 18, 30, 45]
  // }
  LEVEL_THRESHOLDS = [5, 10, 18, 30, 45]
  // Persist thresholds so the global announcer in App can use the same values
  useEffect(() => {
    try { localStorage.setItem('CERT_THRESHOLDS', JSON.stringify(LEVEL_THRESHOLDS)) } catch (_) {}
  }, [userdata && userdata.email])

  console.log('MyTestResume userdata: ', userdata.email);

  const MIN_TESTS_FOR_CERT = 1  // minimum passed tests in an area

  const computeLevel = (lc) => {
    if (!lc || lc <= 0) return 0
    let lvl = 0
    for (let i = 0; i < LEVEL_THRESHOLDS.length; i++) {
      if (lc >= LEVEL_THRESHOLDS[i]) lvl = i + 1
    }
    return lvl
  }

  // Format helpers for display/print without extra descriptors in main category
  const cleanMainCategory = (name) => {
    if (!name) return ''
    // Split on common dash separators and take the first token
    const parts = String(name).split(/\s*[–—-]\s*/)
    return parts[0].trim()
  }
  const getLevelPrefix = (level) => {
    switch (level) {
      case 1: return 'Certificate of Foundational'
      case 2: return 'Certificate of Advanced'
      case 3: return 'Certificate of Distinguished'
      case 4: return 'Certificate of Elite'
      case 5: return 'Certificate of Pinnacle'
      default: return 'Certificate'
    }
  }

  // New naming: Real-Time <LevelName> Certificate In ...
  const getLevelName = (level) => {
    switch (level) {
      case 1: return 'Foundational'
      case 2: return 'Advanced'
      case 3: return 'Distinguished'
      case 4: return 'Elite'
      case 5: return 'Pinnacle'
      default: return ''
    }
  }

  useEffect(() => {
    if (!userdata) return
    const companyID = userdata.real_companyID || userdata.companyID
    const personid = userdata.userID
    const ref = firebase.database.ref(`userTestResults/${companyID}/${personid}`)
    const unsub = ref.on('value', (snap) => {
      const val = snap.val() || {}
      const list = Object.keys(val).map((surveyid) => ({ surveyid, ...val[surveyid] }))
      setRows(list)
      setLoading(false)
    })
    return () => ref.off('value', unsub)
  }, [userdata])

  const passed = useMemo(() => rows
    .map((r) => {
      const total = parseInt(r.totalAnswered || r.qlen || 0)
      const score = parseInt(r.testscore || 0)
      const passedBool = (typeof r.testPassed === 'boolean')
        ? r.testPassed
        : calculateTestPassed(score, total, r.difficulty || 'basic')
      const percentage = total > 0 ? ((score / total) * 100).toFixed(1) : '0.0'
      const lc = typeof r.lcEarned === 'number'
        ? r.lcEarned
        : (passedBool ? calculateLCEarned(score, true, r.difficulty || 'basic') : 0)
      return { ...r, testPassed: passedBool, percentage, lc }
    })
    .filter((r) => r.testPassed)
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  , [rows])

  // Build aggregates once for reuse (listing and printing)
  const aggregates = useMemo(() => {
    const byCatSub = {}
    const bySubAll = {}
    const byCategoryTotals = {}
    passed.forEach((r) => {
      const cat = r.leadCategory || r.categoryType || 'general'
      const sub = r.leadSubCategory || ''
      const lc = Number(r.lc || r.lcEarned || 0)
      if (!sub) return
      byCatSub[cat] = byCatSub[cat] || {}
      byCatSub[cat][sub] = byCatSub[cat][sub] || { lc: 0, tests: 0 }
      byCatSub[cat][sub].lc += lc
      byCatSub[cat][sub].tests += 1

      bySubAll[sub] = bySubAll[sub] || { lc: 0, tests: 0, categories: new Set() }
      bySubAll[sub].lc += lc
      bySubAll[sub].tests += 1
      bySubAll[sub].categories.add(cat)

      // Totals per category across multiple sub-categories
      byCategoryTotals[cat] = byCategoryTotals[cat] || { lc: 0, tests: 0, subs: new Set() }
      byCategoryTotals[cat].lc += lc
      byCategoryTotals[cat].tests += 1
      byCategoryTotals[cat].subs.add(sub)
    })
    return { byCatSub, bySubAll, byCategoryTotals }
  }, [passed])

  // Certificates per client: (1) Specialized: Category → Sub; (2) General: General → Sub (across multiple categories)
  const certificateLists = useMemo(() => {
    const { byCatSub, bySubAll } = aggregates

    const specialized = []
    const general = []

    // Specialized: Category → Sub
    Object.keys(byCatSub).forEach((cat) => {
      Object.keys(byCatSub[cat]).forEach((sub) => {
        const info = byCatSub[cat][sub]
        // keep legacy simple list for compatibility using level 1 threshold
        if (info.lc >= LEVEL_THRESHOLDS[0] && info.tests >= MIN_TESTS_FOR_CERT) {
          specialized.push(`${cat} → ${sub}`)
        }
      })
    })

    // General: General → Sub (must be across multiple categories)
    Object.keys(bySubAll).forEach((sub) => {
      const info = bySubAll[sub]
      if ((info.categories && info.categories.size > 1) && info.lc >= LEVEL_THRESHOLDS[0] && info.tests >= MIN_TESTS_FOR_CERT) {
        general.push(`General → ${sub}`)
      }
    })

    // Previews to assist testing (non-exec): show when LC threshold met even if tests < min
    if (specialized.length === 0) {
      Object.keys(byCatSub).forEach((cat) => {
        Object.keys(byCatSub[cat]).forEach((sub) => {
          const info = byCatSub[cat][sub]
          if (info.lc >= LEVEL_THRESHOLDS[0]) {
            specialized.push(`Preview: ${cat} → ${sub}`)
          }
        })
      })
    }
    if (general.length === 0) {
      Object.keys(bySubAll).forEach((sub) => {
        const info = bySubAll[sub]
        if (info.lc >= LEVEL_THRESHOLDS[0]) {
          general.push(`Preview: General → ${sub}`)
        }
      })
    }

    return {
      specialized: Array.from(new Set(specialized)).sort(),
      general: Array.from(new Set(general)).sort()
    }
  }, [aggregates])

  // Fire a global event ONLY for newly-qualified certificates after initial baseline
  const prevQualifiedRef = useRef({ specialized: [], general: [], seeded: false })
  useEffect(() => {
    try {
      const uid = (userdata && userdata.userID) || 'anon'
      const baselineKey = `CERT_LISTS_BASELINE_${uid}`
      const curSpec = certificateLists.specialized || []
      const curGen = certificateLists.general || []

      // On first run for this user, seed baseline from localStorage or current lists, do not announce
      if (!prevQualifiedRef.current.seeded) {
        let stored = null
        try { stored = JSON.parse(localStorage.getItem(baselineKey) || 'null') } catch (_) { stored = null }
        const baseSpec = stored && Array.isArray(stored.specialized) ? stored.specialized : curSpec
        const baseGen = stored && Array.isArray(stored.general) ? stored.general : curGen
        prevQualifiedRef.current = { specialized: baseSpec, general: baseGen, seeded: true }
        try { localStorage.setItem(baselineKey, JSON.stringify({ specialized: baseSpec, general: baseGen })) } catch (_) {}
        return
      }

      const prev = prevQualifiedRef.current || { specialized: [], general: [] }
      const prevSet = new Set([...(prev.specialized || []), ...(prev.general || [])])
      const curAll = [...curSpec, ...curGen]
      const newOnes = curAll.filter((x) => !prevSet.has(x))
      if (newOnes.length > 0) {
        newOnes.forEach((label) => {
          const friendly = (() => {
            const clean = formatLabel(label)
            if (clean.startsWith('General')) return `Real-Time Certificate In ${clean.replace('General → ', 'Leadership -  ')}`
            if (clean.includes(' → ')) {
              const [cat, sub] = clean.split(' → ')
              return `Real-Time Certificate In ${cat} ${sub}`
            }
            return `Real-Time Certificate In ${clean}`
          })()
          try { window.dispatchEvent(new CustomEvent('CERT_EARNED', { detail: { label: friendly } })) } catch (_) {}
        })
      }
      prevQualifiedRef.current = { specialized: curSpec, general: curGen, seeded: true }
      try { localStorage.setItem(baselineKey, JSON.stringify({ specialized: curSpec, general: curGen })) } catch (_) {}
    } catch (_) {}
  }, [certificateLists, userdata])

  // Map sub-category names to subject phrases
  const subjectForSub = (sub) => {
    const s = String(sub || '').toLowerCase()
    if (s.includes('leading self')) return 'Self Leadership'
    if (s.includes('leading others')) return 'People Leadership'
    if (s.includes('leading organisation') || s.includes('leading organization')) return 'Organisational Leadership'
    if (s.includes('leading society')) return 'Society Leadership'
    // Fallback: use the sub as-is with "Leadership"
    const trimmed = (sub || '').toString().trim()
    if (!trimmed) return 'Leadership'
    return `${trimmed} Leadership`
  }

  // Compute level-based certificates: specialized (Cat→Sub), general (across categories), category-wide (one category across many subs)
  const levelCertificates = useMemo(() => {
    const { byCatSub, bySubAll, byCategoryTotals } = aggregates

    const specialized = []
    const general = []
    const categoryWide = []

    // Specialized: evaluate per Category→Sub pair
    Object.keys(byCatSub).forEach((cat) => {
      Object.keys(byCatSub[cat]).forEach((sub) => {
        const info = byCatSub[cat][sub]
        if (info.tests >= MIN_TESTS_FOR_CERT) {
          const lvl = computeLevel(info.lc)
          if (lvl > 0) {
            const title = `Real-Time ${getLevelName(lvl)} Certificate In ${cleanMainCategory(cat)} ${sub}`
            specialized.push({ type: 'specialized', cat, sub, level: lvl, lc: info.lc, tests: info.tests, title })
          }
        }
      })
    })

    // General: sub across multiple categories
    Object.keys(bySubAll).forEach((sub) => {
      const info = bySubAll[sub]
      const acrossMultipleCats = info.categories && info.categories.size > 1
      if (acrossMultipleCats && info.tests >= MIN_TESTS_FOR_CERT) {
        const lvl = computeLevel(info.lc)
        if (lvl > 0) {
          const title = `Real-Time ${getLevelName(lvl)} Certificate In Leadership -  ${sub}`
          general.push({ type: 'general', sub, level: lvl, lc: info.lc, tests: info.tests, title })
        }
      }
    })

    // Category-wide: one category across multiple sub-categories
    Object.keys(byCategoryTotals).forEach((cat) => {
      const info = byCategoryTotals[cat]
      const acrossMultipleSubs = info.subs && info.subs.size > 1
      if (acrossMultipleSubs && info.tests >= MIN_TESTS_FOR_CERT) {
        const lvl = computeLevel(info.lc)
        if (lvl > 0) {
          const title = `Real-Time ${getLevelName(lvl)} Certificate In ${cleanMainCategory(cat)} Leadership`
          categoryWide.push({ type: 'category', cat, level: lvl, lc: info.lc, tests: info.tests, title })
        }
      }
    })

    // Keep only the highest level per key to avoid duplicates (e.g., multiple entries if UI recalculates)
    const pickMaxLevel = (arr, keyFn) => {
      const map = new Map()
      arr.forEach((it) => {
        const k = keyFn(it)
        const prev = map.get(k)
        if (!prev || it.level > prev.level) map.set(k, it)
      })
      return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title))
    }

    return {
      specialized: pickMaxLevel(specialized, (i) => `${i.cat}::${i.sub}`),
      general: pickMaxLevel(general, (i) => i.sub),
      categoryWide: pickMaxLevel(categoryWide, (i) => i.cat)
    }
  }, [aggregates])

  // Upcoming certificates (within 1 LC of next threshold)
  const upcomingCertificates = useMemo(() => {
    const { byCatSub, bySubAll, byCategoryTotals } = aggregates

    const specialized = []
    const general = []
    const categoryWide = []

    const addUpcoming = (obj) => {
      const currentLevel = computeLevel(obj.lc)
      const hasNext = currentLevel < LEVEL_THRESHOLDS.length
      if (!hasNext) return
      const nextThreshold = LEVEL_THRESHOLDS[currentLevel]
      const shortRaw = nextThreshold - obj.lc
      if (shortRaw > 0 && shortRaw <= 1) {
        const roundedShort = Math.round(shortRaw * 10) / 10
        if (roundedShort <= 0) return // avoid showing 0.0 LC short
        const nextLevel = currentLevel + 1
        const base = { ...obj, nextLevel, short: roundedShort }
        if (obj.type === 'specialized') {
          const title = `Real-Time ${getLevelName(nextLevel)} Certificate In ${cleanMainCategory(obj.cat)} ${obj.sub}`
          specialized.push({ ...base, title })
        } else if (obj.type === 'general') {
          const title = `Real-Time ${getLevelName(nextLevel)} Certificate In Leadership -  ${obj.sub}`
          general.push({ ...base, title })
        } else if (obj.type === 'category') {
          const title = `Real-Time ${getLevelName(nextLevel)} Certificate In ${cleanMainCategory(obj.cat)} Leadership`
          categoryWide.push({ ...base, title })
        }
      }
    }

    // Specialized
    Object.keys(byCatSub).forEach((cat) => {
      Object.keys(byCatSub[cat]).forEach((sub) => {
        const info = byCatSub[cat][sub]
        addUpcoming({ type: 'specialized', cat, sub, lc: info.lc, tests: info.tests })
      })
    })

    // General (across multiple categories)
    Object.keys(bySubAll).forEach((sub) => {
      const info = bySubAll[sub]
      const acrossMultipleCats = info.categories && info.categories.size > 1
      if (acrossMultipleCats) addUpcoming({ type: 'general', sub, lc: info.lc, tests: info.tests })
    })

    // Category-wide (across multiple sub-categories)
    Object.keys(byCategoryTotals).forEach((cat) => {
      const info = byCategoryTotals[cat]
      const acrossMultipleSubs = info.subs && info.subs.size > 1
      if (acrossMultipleSubs) addUpcoming({ type: 'category', cat, lc: info.lc, tests: info.tests })
    })

    const sortByTitle = (a, b) => a.title.localeCompare(b.title)
    return {
      specialized: specialized.sort(sortByTitle),
      general: general.sort(sortByTitle),
      categoryWide: categoryWide.sort(sortByTitle)
    }
  }, [aggregates])

  const handlePrint = () => {
    window.print()
  }

  const getCertStats = (certLabel) => {
    // Return {title, lc, tests}
    const label = certLabel.startsWith('Preview: ') ? certLabel.replace('Preview: ', '') : certLabel
    if (label.startsWith('General → ')) {
      const sub = label.replace('General → ', '')
      const info = aggregates.bySubAll[sub]
      return { title: `General → ${sub}`, lc: info ? info.lc : 0, tests: info ? info.tests : 0 }
    }
    const parts = label.split(' → ')
    if (parts.length === 2) {
      const cat = parts[0]
      const sub = parts[1]
      const info = (aggregates.byCatSub[cat] && aggregates.byCatSub[cat][sub]) || { lc: 0, tests: 0 }
      return { title: `${cat} → ${sub}`, lc: info.lc, tests: info.tests }
    }
    // Fallback: treat as sub-only general
    const info = aggregates.bySubAll[label]
    return { title: `General → ${label}`, lc: info ? info.lc : 0, tests: info ? info.tests : 0 }
  }

  const formatLabel = (label) => {
    const isPreview = label.startsWith('Preview: ')
    const raw = isPreview ? label.replace('Preview: ', '') : label
    if (raw.startsWith('General → ')) {
      return (isPreview ? 'Preview: ' : '') + raw // General labels are already simple
    }
    if (raw.includes(' → ')) {
      const [cat, sub] = raw.split(' → ')
      const clean = cleanMainCategory(cat)
      return (isPreview ? 'Preview: ' : '') + `${clean} → ${sub}`
    }
    return label
  }

  const printLevelCertificate = (item) => {
    // item: { type, cat?, sub?, level, title, lc, tests }
    // Build header and key references
    const certificateDisplayName = item.title // Already like: "Certificate of Foundational Main/Sub"
    const headerTitle = `Me‑Vision Academy: ${certificateDisplayName}`

    const getAreaPassedTitles = (target, limit = 3) => {
      try {
        const list = passed.filter((r) => {
          if (target.type === 'specialized') {
            return (cleanMainCategory(r.leadCategory || r.categoryType || 'general') === cleanMainCategory(target.cat)) && (r.leadSubCategory || '') === target.sub
          }
          if (target.type === 'general') {
            return (r.leadSubCategory || '') === target.sub
          }
          if (target.type === 'category') {
            return (cleanMainCategory(r.leadCategory || r.categoryType || 'general') === cleanMainCategory(target.cat))
          }
          return false
        })
        const titles = list.map((r) => (r.title || r.surveyname || r.surveyid || '')).filter((t) => t && String(t).trim() !== '')
        return Array.from(new Set(titles)).slice(0, limit)
      } catch (_) { return [] }
    }

    const keyThemes = getAreaPassedTitles(item)

    // Context line for specialized/category vs general
    let contextLine = ''
    if (item.type === 'specialized') {
      contextLine = `Category: ${cleanMainCategory(item.cat)} • Sub-category: ${item.sub}`
    } else if (item.type === 'general') {
      contextLine = `General recognition across multiple categories • Sub-category: ${item.sub}`
    } else if (item.type === 'category') {
      contextLine = `Category-wide recognition across multiple sub-categories • Category: ${cleanMainCategory(item.cat)}`
    }

    const now = new Date()
    const dateFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: 'long', day: 'numeric' }).format(now)
    const weekdayFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', weekday: 'long' }).format(now)
    const timeFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', hour: 'numeric', minute: '2-digit', hour12: true }).format(now)
    const t1 = String(timeFmt).toLowerCase().replace(/\s/g, '') // e.g. 5:17pm
    const timeWithMeridiemSpaced1 = t1.replace(/(am|pm)$/, ' $1') // e.g. 5:17 pm
    const validatedOn = `${timeWithMeridiemSpaced1}SAST, ${weekdayFmt}, ${dateFmt}`
    const lcText = `${Number(item.lc || 0).toFixed(2)} LC`

    const certHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${headerTitle}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: "Georgia", "Times New Roman", serif; color: #1c1c1c; }
    .frame { border: 12px solid #d4af37; padding: 24px; border-radius: 8px; }
    .inner { border: 2px solid #b08d57; padding: 24px 32px; border-radius: 6px; display: flex; flex-direction: column; min-height: 210mm; }
    .header { text-align: center; margin-bottom: 18px; }
    .brand { font-size: 12px; letter-spacing: 2px; color: #666; text-transform: uppercase; }
    .title { font-size: 20px; font-weight: 700; margin-top: 6px; }
    .divider { height: 1px; background: #ddd; margin: 16px 0 22px; }
    .block { margin: 10px 0; font-size: 14px; line-height: 1.6; }
    .name { font-size: 20px; font-weight: 700; margin: 4px 0; }
    .certname { font-size: 18px; font-weight: 700; }
    .muted { color: #555; }
    .em { font-style: italic; }
    .row { margin: 6px 0; font-size: 13px; }
    .content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
    .foot { display: flex; justify-content: center; margin-top: 18px; margin-bottom: 22px; }
    .sigCenter { text-align: center; width: 280px; }
    .sigCenter img.signature { height: 143px; object-fit: contain; display: block; margin: 0 auto -10px auto; }
    .sigCenter .line { width: 182px; border-top: 2px solid #000; height: 0; margin: 0 auto 6px auto; }
    .sigCenter .who { font-weight: 700; font-size: 14px; }
    .sigCenter .role { font-size: 12px; color: #444; }
  </style>
  <script>window.onload = function(){ window.print(); }</script>
  </head>
<body>
  <div class="frame">
    <div class="inner">
      <div class="content">
        <div class="header">
          <div class="brand">Me‑Vision on ThinkLead</div>
          <div class="title">${headerTitle}</div>
        </div>
        <div class="divider"></div>
        <div class="block muted">This certifies that</div>
        <div class="name">${userdata.firstName} ${userdata.lastName}</div>
        <div class="block">has successfully completed the rigorous testing requirements for the</div>
        <div class="certname">${certificateDisplayName}</div>
        <div class="block">${contextLine}</div>
        <div class="block">within the Me‑Vision Ecosystem, demonstrating unwavering commitment to the Global Improvement Revolution.</div>
        <div class="block">Through dedication and mastery of key themes assessed, this leader has advanced their own and society’s standard of leadership.</div>
        <div class="row">This achievement, validated on <b>${validatedOn}</b>, reflects their role as a Valuable Leader in society, earning <b>${lcText}</b> in recognition of their impact.</div>
      </div>
      <div class="foot">
        <div class="sigCenter">
          <img class="signature" src="${adriaanSignature}" alt="Signature of Adriaan Groenewald" />
          <div class="line"></div>
          <div class="who">Adriaan Groenewald</div>
          <div class="role">Me‑Vision on ThinkLead Team<br/>Co‑Founder and CEO • ThinkLead.app</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`
    const w = window.open('', '_blank', 'width=900,height=700')
    if (!w) return
    w.document.open()
    w.document.write(certHtml)
    w.document.close()
  }

  const printCertificate = (certLabel) => {
    const stats = getCertStats(certLabel)
    const isPreview = certLabel.startsWith('Preview: ')
    const cleanLabel = isPreview ? certLabel.replace('Preview: ', '') : certLabel
    // Build a human-friendly certificate name from label (Category/Sub path)
    let certificateName = ''
    if (cleanLabel.startsWith('General → ')) {
      const sub = cleanLabel.replace('General → ', '')
      certificateName = `General/${sub}`
    } else if (cleanLabel.includes(' → ')) {
      const [cat, sub] = cleanLabel.split(' → ')
      certificateName = `${cleanMainCategory(cat)}/${sub}`
    } else {
      certificateName = cleanLabel
    }

    // Format validation date/time in SAST (Africa/Johannesburg)
    const now = new Date()
    const dateFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', year: 'numeric', month: 'long', day: 'numeric' }).format(now)
    const weekdayFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', weekday: 'long' }).format(now)
    const timeFmt = new Intl.DateTimeFormat('en-ZA', { timeZone: 'Africa/Johannesburg', hour: 'numeric', minute: '2-digit', hour12: true }).format(now)
    const t2 = String(timeFmt).toLowerCase().replace(/\s/g, '') // e.g. 5:17pm
    const timeWithMeridiemSpaced2 = t2.replace(/(am|pm)$/, ' $1') // e.g. 5:17 pm
    const validatedOn = `${timeWithMeridiemSpaced2}SAST, ${weekdayFmt}, ${dateFmt}`

    const lcText = `${Number(stats.lc).toFixed(2)} LC`
    const headerTitle = `Me‑Vision Academy: Certificate – ${certificateName}`

    const certHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${headerTitle}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: "Georgia", "Times New Roman", serif; color: #1c1c1c; }
    .frame { border: 12px solid #d4af37; padding: 24px; border-radius: 8px; }
    .inner { border: 2px solid #b08d57; padding: 24px 32px; border-radius: 6px; display: flex; flex-direction: column; min-height: 210mm; }
    .header { text-align: center; margin-bottom: 18px; }
    .brand { font-size: 12px; letter-spacing: 2px; color: #666; text-transform: uppercase; }
    .title { font-size: 20px; font-weight: 700; margin-top: 6px; }
    .divider { height: 1px; background: #ddd; margin: 16px 0 22px; }
    .block { margin: 10px 0; font-size: 14px; line-height: 1.6; }
    .name { font-size: 20px; font-weight: 700; margin: 4px 0; }
    .certname { font-size: 18px; font-weight: 700; }
    .muted { color: #555; }
    .em { font-style: italic; }
    .row { margin: 6px 0; font-size: 13px; }
    .content { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; }
    .foot { display: flex; justify-content: space-between; margin-top: 22px; align-items: flex-end; }
    .sig { text-align: left; }
    .sig .line { width: 240px; border-top: 1px solid #000; height: 0; margin: 28px 0 6px; }
    .sig .who { font-weight: 700; font-family: "Brush Script MT", "Lucida Handwriting", cursive; font-size: 22px; }
    .sig .role { font-size: 12px; color: #444; }
    .sigRight { text-align: center; }
    .sigRight .line { width: 240px; border-top: 2px solid #000; height: 0; margin: 6px auto 6px auto; display: block; }
    .sigRight .role { font-size: 12px; color: #444; }
    .sigRight img { height: 120px; object-fit: contain; display: block; margin: 0 auto; }
  </style>
  <script>window.onload = function(){ window.print(); }</script>
  </head>
<body>
  <div class="frame">
    <div class="inner">
      <div class="content">
        <div class="header">
          <div class="brand">Me‑Vision on ThinkLead</div>
          <div class="title">${headerTitle}</div>
        </div>
        <div class="divider"></div>
        <div class="block muted">This certifies that</div>
        <div class="name">${userdata.firstName} ${userdata.lastName}</div>
        <div class="block">has successfully completed the rigorous testing requirements for the</div>
        <div class="certname">Certificate – ${certificateName}</div>
        <div class="block">within the Me‑Vision Ecosystem, demonstrating unwavering commitment to the Global Improvement Revolution.</div>
        <div class="block">Through dedication and mastery of key themes assessed, this leader has advanced their own and society’s standard of leadership.</div>
        <div class="row">This achievement, validated on <b>${validatedOn}</b>, reflects their role as a Valuable Leader in society, earning <b>${lcText}</b> in recognition of their impact.</div>
      </div>
      <div class="foot">
        <div class="sig">
          <div class="who">Adriaan Groenewald</div>
          <div class="role">Me‑Vision on ThinkLead Team<br/>Co‑Founder and CEO • ThinkLead.app</div>
        </div>
        <div class="sigRight">
          <img src="${adriaanSignature}" alt="Signature of Adriaan Groenewald" />
          <div class="role">Signature</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`
    const w = window.open('', '_blank', 'width=900,height=700')
    if (!w) return
    w.document.open()
    w.document.write(certHtml)
    w.document.close()
  }

  const openPassedTestsList = () => {
    try {
      const safe = (v) => String(v == null ? '' : v)
      const escapeHtml = (str) => safe(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\"/g, '&quot;')
        .replace(/'/g, '&#039;')
      const truncate = (str, max = 140) => {
        const s = safe(str)
        if (s.length <= max) return s
        return s.slice(0, max - 1) + '…'
      }

      const rows = passed.map((r) => {
        const titleFull = r.title || r.surveyname || r.surveyid || ''
        const titleShort = truncate(titleFull, 140)
        return {
        date: r.timestamp ? new Date(r.timestamp).toLocaleDateString() : '-',
        title: titleFull,
        titleShort,
        category: r.leadCategory || r.categoryType || 'general',
        sub: r.leadSubCategory || '',
        medium: r.mediumType || 'content',
        percentage: r.percentage || '0.0',
        lc: Number(r.lc || r.lcEarned || 0).toFixed(2)
      }
      })

      const csvHeader = ['Date', 'Title', 'Category', 'Sub-category', 'Medium', 'Score %', 'LC']
      const csvEscape = (s) => '"' + String(s).replace(/"/g, '""') + '"'
      const csvLines = [
        csvHeader.map(csvEscape).join(','),
        ...rows.map((row) => [row.date, row.title, row.category, row.sub, row.medium, row.percentage, row.lc].map(csvEscape).join(','))
      ]
      const csvUri = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvLines.join('\n'))

      // Group rows by Category → Sub-category
      const groupMap = {}
      rows.forEach((row) => {
        const key = row.category + (row.sub ? ' → ' + row.sub : '')
        if (!groupMap[key]) groupMap[key] = []
        groupMap[key].push(row)
      })
      const groupKeys = Object.keys(groupMap).sort((a, b) => a.localeCompare(b))
      const rowsHtml = groupKeys.map((key) => {
        const items = groupMap[key]
        const header = `<tr class="group"><td colspan="6">${escapeHtml(key)} <span class="count">(${items.length})</span></td></tr>`
        const itemRows = items.map((row) => (
          `<tr>` +
            `<td>${escapeHtml(row.date)}</td>` +
            `<td>${escapeHtml(row.titleShort)}</td>` +
            `<td>${escapeHtml(row.category)}${row.sub ? ' → ' + escapeHtml(row.sub) : ''}</td>` +
            `<td>${escapeHtml(row.medium)}</td>` +
            `<td>${escapeHtml(row.percentage)}%</td>` +
            `<td>${escapeHtml(row.lc)}</td>` +
          `</tr>`
        )).join('')
        return header + itemRows
      }).join('')

      const headerTitle = `Passed Tests – ${userdata.firstName || ''} ${userdata.lastName || ''}`
      const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${headerTitle}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    body { font-family: "Georgia", "Times New Roman", serif; color: #1c1c1c; }
    .frame { border: 12px solid #d4af37; padding: 20px; border-radius: 8px; }
    .inner { border: 2px solid #b08d57; padding: 16px 20px; border-radius: 6px; }
    .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
    .title { font-size: 18px; font-weight: 700; }
    .meta { font-size: 12px; color: #555; }
    .actions { display: flex; gap: 8px; }
    .btn { background: #111; color: #fff; padding: 6px 10px; border-radius: 6px; text-decoration: none; font-size: 12px; display: inline-block; }
    .btn.secondary { background: #f5f5f5; color: #111; border: 1px solid #ddd; }
    .btn.icon { background: #f5f5f5; color: #111; border: 1px solid #ddd; width: 26px; height: 26px; border-radius: 13px; display: inline-flex; align-items: center; justify-content: center; padding: 0; font-size: 14px; line-height: 1; }
    table { width: 100%; border-collapse: collapse; }
    th, td { font-size: 12px; text-align: left; padding: 6px; }
    thead th { border-bottom: 1px solid #ddd; background: #fafafa; }
    tbody tr:nth-child(even) { background: #fcfcfc; }
    tr.group td { background: #fff8dc; font-weight: bold; border-top: 1px solid #eee; }
    .count { color: #777; font-weight: normal; }
  </style>
  <script>window.addEventListener('keydown', function(e){ if((e.ctrlKey||e.metaKey)&&e.key==='p'){ setTimeout(()=>window.print(), 0); } if(e.key==='Escape'){ window.close(); }});</script>
  </head>
<body>
  <div class="frame">
    <div class="inner">
      <div class="header">
        <div>
          <div class="title">${headerTitle}</div>
          <div class="meta">Total: ${rows.length} • Company: ${escapeHtml(userdata.companyName || '')}</div>
        </div>
        <div class="actions">
          <a class="btn icon" href="#" onclick="window.close(); return false;" title="Close">×</a>
          <a class="btn" href="#" onclick="window.print(); return false;">Print</a>
          <a class="btn secondary" href="${csvUri}" download="passed-tests-${new Date().toISOString().slice(0,10)}.csv">Download CSV</a>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Title</th>
            <th>Category → Sub-category</th>
            <th>Medium</th>
            <th>Score %</th>
            <th>LC</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  </div>
</body>
</html>`
      const w = window.open('', '_blank', 'width=1000,height=800')
      if (!w) return
      w.document.open()
      w.document.write(html)
      w.document.close()
    } catch (err) {
      console.error('Failed to open passed tests list', err)
    }
  }

  if (!userdata) return <div style={{ padding: 16 }}>Please sign in.</div>

  // Mobile-first compact UI
  if (isMobile) {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 'bold' }}>My Resume</div>
            <div style={{ fontSize: 11, color: '#666' }}>{userdata.firstName} {userdata.lastName}</div>
          </div>
          <button onClick={handlePrint} style={{ background: '#111', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>Print</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, color: '#777' }}>Certificates</div>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>{(certificateLists.specialized.length + certificateLists.general.length)}</div>
          </div>
          <div style={{ background: '#fafafa', border: '1px solid #eee', borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 11, color: '#777' }}>Passed Tests</div>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>{passed.length}</div>
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Certificates (Levels)</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {levelCertificates.specialized.length === 0 && levelCertificates.general.length === 0 && levelCertificates.categoryWide.length === 0 && (
                <div style={{ fontSize: 12, color: '#777' }}>None</div>
              )}
              {[...levelCertificates.specialized, ...levelCertificates.general, ...levelCertificates.categoryWide].map((it) => (
                <div key={`${it.type}:${it.cat || ''}:${it.sub || ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 12 }}>{it.title}</div>
                  <button onClick={() => printLevelCertificate(it)} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 11, padding: '4px 8px', borderRadius: 6, cursor: 'pointer' }}>Print</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming certificates (within 1 LC of next level) */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Upcoming certificates</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(() => {
                const upAll = [...upcomingCertificates.specialized, ...upcomingCertificates.general, ...upcomingCertificates.categoryWide]
                if (upAll.length === 0) return <div style={{ fontSize: 12, color: '#777' }}>None</div>
                return upAll.map((it) => (
                  <div key={`up:${it.type}:${it.cat || ''}:${it.sub || ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f5f5f5', border: '1px dashed #ddd', borderRadius: 8, padding: '10px 12px', color: '#666' }}>
                    <div style={{ fontSize: 12 }}>{`${it.title} - [${it.short.toFixed(1)} Lc short]`}</div>
                  </div>
                ))
              })()}
            </div>
          )}
        </div>

        <div>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Passed Tests</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : passed.length === 0 ? (
            <div style={{ fontSize: 12, color: '#777' }}>No passed tests yet.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: '#444' }}>Total passed tests: <b>{passed.length}</b></div>
              <button onClick={openPassedTestsList} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 12, padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>Open printable & downloadable list</button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>Personal Development Certificate</div>
          <div style={{ fontSize: 12, color: '#666' }}>{userdata.firstName} {userdata.lastName} • {userdata.companyName}</div>
        </div>
        <button onClick={handlePrint} style={{ background: '#000', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>Print</button>
      </div>

      <div ref={printRef} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, padding: 20 }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 22, fontWeight: 'bold' }}>Certificate of Progress</div>
          <div style={{ fontSize: 12, color: '#555' }}>This document summarizes tests successfully completed on ThinkLead</div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Certificates Qualified (Levels)</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : (
            <>
              <div style={{ fontSize: 12, marginBottom: 6, color: '#444' }}><b>Specialized (Category → Sub-category)</b></div>
              {levelCertificates.specialized.length === 0 ? (
                <div style={{ fontSize: 12, color: '#777', marginBottom: 8 }}>None</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16, marginBottom: 10 }}>
                  {levelCertificates.specialized.map((it) => (
                    <li key={`${it.type}:${it.cat}:${it.sub}`} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{it.title}</span>
                      <button onClick={() => printLevelCertificate(it)} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 11, padding: '2px 6px', borderRadius: 4, cursor: 'pointer' }}>Print</button>
                    </li>
                  ))}
                </ul>
              )}

              <div style={{ fontSize: 12, marginBottom: 6, color: '#444' }}><b>General (across categories → Sub-category)</b></div>
              {levelCertificates.general.length === 0 ? (
                <div style={{ fontSize: 12, color: '#777' }}>None</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {levelCertificates.general.map((it) => (
                    <li key={`${it.type}:${it.sub}`} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{it.title}</span>
                      <button onClick={() => printLevelCertificate(it)} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 11, padding: '2px 6px', borderRadius: 4, cursor: 'pointer' }}>Print</button>
                    </li>
                  ))}
                </ul>
              )}

              <div style={{ fontSize: 12, marginBottom: 6, color: '#444' }}><b>Category-wide (one category → many sub-categories)</b></div>
              {levelCertificates.categoryWide.length === 0 ? (
                <div style={{ fontSize: 12, color: '#777' }}>None</div>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {levelCertificates.categoryWide.map((it) => (
                    <li key={`${it.type}:${it.cat}`} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{it.title}</span>
                      <button onClick={() => printLevelCertificate(it)} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 11, padding: '2px 6px', borderRadius: 4, cursor: 'pointer' }}>Print</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Upcoming certificates</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : (
            (() => {
              const upAll = [...upcomingCertificates.specialized, ...upcomingCertificates.general, ...upcomingCertificates.categoryWide]
              if (upAll.length === 0) {
                return <div style={{ fontSize: 12, color: '#777' }}>None</div>
              }
              return (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
                  {upAll.map((it) => (
                    <li key={`up:${it.type}:${it.cat || ''}:${it.sub || ''}`} style={{ display: 'block' }}>
                      <div style={{ fontSize: 12, color: '#666', background: '#f5f5f5', border: '1px dashed #ddd', borderRadius: 8, padding: '8px 10px', display: 'inline-block', width: 'fit-content' }}>
                        {`${it.title} - [${it.short.toFixed(1)} Lc short]`}
                      </div>
                    </li>
                  ))}
                </ul>
              )
            })()
          )}
        </div>

        <div style={{ marginBottom: 10 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Learner</div>
          <div style={{ fontSize: 12 }}>{userdata.firstName} {userdata.lastName}</div>
          <div style={{ fontSize: 12 }}>{userdata.email}</div>
          <div style={{ fontSize: 12 }}>{userdata.companyName}</div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 6 }}>Passed Tests</div>
          {loading ? (
            <div style={{ fontSize: 12, color: '#777' }}>Loading…</div>
          ) : passed.length === 0 ? (
            <div style={{ fontSize: 12, color: '#777' }}>No passed tests yet.</div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 12, color: '#444' }}>Total passed tests: <b>{passed.length}</b></div>
              <button onClick={openPassedTestsList} style={{ border: '1px solid #ddd', background: '#f9f9f9', fontSize: 12, padding: '6px 10px', borderRadius: 6, cursor: 'pointer' }}>Open printable & downloadable list</button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({ userdata: state.userDialog.userDetails[0] || null })
export default connect(mapStateToProps)(MyTestResume)


